import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";

import { AuthHelper } from "#core/helpers/auth.helper";
import { UsherPayloadHelper } from "#core/helpers/usher-payload.helper";
import { CookieService } from "#core/services";
import { LocalStorage } from "#core/services/local-storage.service";
import { VersionService } from "#core/services/version.service";
import { TimezoneHelper } from "#helpers/timezone.helper";
import { SnackBarService } from "#services/snackbar.service";
import { USER_SERVICE } from "@carnegie-learning/data-access-angular-fido";
import { Auth, DistrictsFidoService, ProfileService, SchoolsFidoService } from "@carnegie-learning/data-access-angular-passport";
import { UserService } from "@carnegie-learning/data-access-fido";
import * as loginFormActions from "../actions/login-form.action";

@Injectable()
export class LoginProfileEffect {
    constructor(
        private actions$: Actions,
        private authHelper: AuthHelper,
        private localStorage: LocalStorage,
        private profileService: ProfileService,
        private timezoneHelper: TimezoneHelper,
        private router: Router,
        private snackBarService: SnackBarService,
        @Inject(USER_SERVICE) private userService: UserService,
        private auth: Auth,
        private schoolsFidoService: SchoolsFidoService,
        private districtFidoService: DistrictsFidoService,
        private versionService: VersionService,
        private usherPayloadHelper: UsherPayloadHelper,
        private cookieService: CookieService
    ) {}

    // Retrieves User Profile then triggers corresponding actions
    userProfileAttempt$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userProfileAttempt)).pipe(
            switchMap((action) => {
                // @ts-ignore
                return this.profileService.getProfile().pipe(
                    map((profileData) => {
                        let date: any = this.timezoneHelper.convertServerDateToUserTZ(
                            profileData.body.accessTokenExpireAt,
                            profileData.body.timezone
                        );
                        date = date.toString();
                        this.localStorage.set(
                            "authToken",
                            profileData.body.accessToken,
                            date
                        );

                        this.localStorage.set("userProfile", profileData.body);
                        return loginFormActions.userProfileSuccess({
                            payload: profileData.body,
                        });
                    }),
                    catchError((error) =>
                        of(loginFormActions.userProfileFail(error))
                    )
                );
            })
        )
    );

    // Digests the user profile data
    userProfileSuccess$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userProfileSuccess)).pipe(
            map((action) => {
                return loginFormActions.userProfileSave({
                    payload: action.payload,
                });
            })
        )
    );

    // Saves profile in state and trigger redirect state
    userProfileSave$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userProfileSave)).pipe(
            map((action) => {
                // if (
                //     action.payload.tempPassword !== null &&
                //     (<any>window).isDemo
                // ) {
                //     this.router.navigate(["temp-password/form"]);
                //     return loginFormActions.userTempPwdDetected();
                // } else {
                //     this.auth.redirectUser(action.payload);
                //     return loginFormActions.userRedirect();
                // }
                this.auth.redirectUser(action.payload);
                return loginFormActions.loadFidoUserData({
                    fidoUserID: action.payload.userGUID,
                });
            })
        )
    );

    // Refresh User Profile then triggers corresponding actions
    userProfileRefresh$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userProfileRefresh)).pipe(
            switchMap((action) => {
                return this.profileService.getProfile();
            }),
            switchMap((resp) => {
                // if (
                //     resp.body.tempPassword !== null &&
                //     (<any>window).isDemo
                // ) {
                //     this.router.navigate(["temp-password/form"]);
                //     return [
                //         loginFormActions.userTempPwdDetected(),
                //         loginFormActions.userProfileUpdated({
                //             payload: resp.body
                //         })
                //     ];
                // } else {
                let date: any = this.timezoneHelper.convertServerDateToUserTZ(
                    resp.body.accessTokenExpireAt,
                    resp.body.timezone
                );

                this.versionService.redirectToCorrectVersion();

                let returnedProfile = resp.body;

                // TeacherStudent impersonation - retain student profile if backup profile exists
                const teacherStudentProfile = this.localStorage.get('userProfile_backup');
                if (teacherStudentProfile) {
                    // teacher student impersonation need to keep profile as is
                    returnedProfile = teacherStudentProfile;
                } else {
                    // normal profile request
                    date = date.toString();
                    this.localStorage.set("authToken", returnedProfile.accessToken, date);
                    this.localStorage.set("userProfile", returnedProfile);
                }

                const currentTZ = this.localStorage.get("currentTimeZone");

                // set default TZ for user
                // if (
                //     resp.body.roleLevel === 4 &&
                //     currentTZ === null &&
                //     resp.body.schools.length > 0
                // ) {
                //     this.localStorage.set(
                //         "currentTimeZone",
                //         resp.body.schools[0].timezone
                //     );
                // } else
                if (currentTZ !== null) {
                    // do nothing and keep whatever was set, refresh should not alter a tz that was already set
                } else {
                    this.localStorage.set("currentTimeZone", "America/Chicago");
                }

                return [
                    loginFormActions.userProfileUpdated({
                        payload: returnedProfile,
                    }),
                ];
                // }
            })
        )
    );

    userProfileUpdated = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.userProfileUpdated)).pipe(
            map((action) => {
                return loginFormActions.loadFidoUserData({
                    fidoUserID: action.payload.userGUID,
                });
            })
        )
    );

    // Retrieve Fido User Data
    loadFidoUserData$ = createEffect(() =>
        this.actions$.pipe(ofType(loginFormActions.loadFidoUserData)).pipe(
            switchMap((action) => {
                return this.userService.getUser(action.fidoUserID).pipe(
                    map((response) => {
                        let updatedProfile = this.localStorage.get(
                            "userProfile"
                        );
                        updatedProfile = {
                            ...updatedProfile,
                            firstName: response.givenName,
                            lastName: response.surName,
                            fullName:
                                response.givenName + " " + response.surName,
                            eAddress: response.email,
                            userGUID: response.id, // we need uuid, just to make sure it is assigned.
                        };

                        this.localStorage.set("userProfile", updatedProfile);
                        this.localStorage.set("fidoUserProfile", response);

                        return loginFormActions.loadFidoUserDataSuccess({
                            fidoUserData: response,
                        });
                    }),
                    catchError((error) =>
                        of(
                            loginFormActions.loadFidoUserDataFail({
                                data: error,
                            })
                        )
                    )
                );
            })
        )
    );

    // Trigger Retrieve Fido User School and District Data
    triggerLoadFidoUserSchoolDistrictData$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.loadFidoUserDataSuccess))
            .pipe(
                map(action => {
                    return loginFormActions.loadFidoUserSchoolDistrictData();
                })
            )
    );

    // Retrieve Fido User School and District Data
    loadFidoUserSchoolDistrictData$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.loadFidoUserSchoolDistrictData))
            .pipe(
                mergeMap(() => {
                    const userProfile = this.localStorage.get("userProfile");
                    const existingSchoolData = this.localStorage.get("fidoSchoolList");

                    let schoolIdFromCookie = this.cookieService.get('CL_SCHOOL');
                    // Only call this once, no need to call this more than necessary
                    // PEN-142 Local storage and Cookie data would get mismatched on multiple user logins, so call again to correct it.
                    if (!existingSchoolData || (existingSchoolData[0] && existingSchoolData[0].schoolid && schoolIdFromCookie != existingSchoolData[0].schoolid)) {
                        if (userProfile.schools?.length) {
                            return this.schoolsFidoService.getSchools(userProfile.schools).pipe(
                                mergeMap(schoolData => {
                                    if (schoolData?.list?.length) {
                                        const fidoSchoolList = schoolData.list;
                                        this.localStorage.set("fidoSchoolList", fidoSchoolList);

                                        if (fidoSchoolList[0].districtId) {
                                            return this.districtFidoService.getDistrict(fidoSchoolList[0].districtId).pipe(
                                                map(districtData => {
                                                    if (districtData) {
                                                        this.localStorage.set("fidoDistrictData", districtData);
                                                    }

                                                    return true;
                                                })
                                            );
                                        }
                                    }

                                    return of(true);
                                }),
                                catchError(() => of(false))
                            );
                        }

                        return of(false);
                    }

                    return of(true);
                }),
                map(successOrFail => successOrFail ? loginFormActions.loadFidoUserSchoolDistrictDataSuccess()
                                                   : loginFormActions.loadFidoUserSchoolDistrictDataFail())
            )
    );

    initializeUsher$ = createEffect(
        () => this.actions$.pipe(
            ofType(loginFormActions.loadFidoUserSchoolDistrictDataSuccess),
            tap(() => {
                this.usherPayloadHelper.initializeUser();
            })
        ),
        { dispatch: false }
    );

    // Updating the user's profile data
    userProfileUpdateAttempt$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.userProfileUpdateAttempt))
            .pipe(
                switchMap((action) => {
                    return this.profileService
                        .updateProfile(action.payload)
                        .pipe(
                            map((response) =>
                                loginFormActions.userProfileUpdateSuccess({
                                    data: response,
                                })
                            ),
                            catchError((error) =>
                                of(
                                    loginFormActions.userProfileUpdateFail({
                                        data: error,
                                    })
                                )
                            )
                        );
                })
            )
    );

    // Save the updated User Profile Data
    userProfileUpdateSuccess$ = createEffect(() =>
        this.actions$
            .pipe(ofType(loginFormActions.userProfileUpdateSuccess))
            .pipe(
                map((action) => {
                    const logoutRedirectURL = this.localStorage.get(
                        "logoutRedirectURL"
                    );
                    const requestedProgramID: number = this.localStorage.get(
                        "requestProgramID"
                    );
                    const fidoUserData = this.localStorage.get(
                        "fidoUserProfile"
                    );

                    // Must be after the clear, url needs to persist
                    if (
                        ((<any>window).isDemo ||
                            (<any>window).env === "s" ||
                            (<any>window).env === "local") &&
                        logoutRedirectURL !== undefined &&
                        logoutRedirectURL !== null &&
                        logoutRedirectURL !== "null" &&
                        logoutRedirectURL !== ""
                    ) {
                        this.localStorage.set(
                            "logoutRedirectURL",
                            logoutRedirectURL
                        );
                        this.localStorage.set(
                            "requestProgramID",
                            requestedProgramID
                        );
                    }

                    let date: any = this.timezoneHelper.convertServerDateToUserTZ(
                        action.data.accessTokenExpireAt,
                        action.data.timezone
                    );
                    date = date.toString();
                    this.localStorage.set(
                        "authToken",
                        action.data.accessToken,
                        date
                    );

                    // Need to update meta data
                    let profileDataWithFidoData = action.data;
                    if (fidoUserData) {
                        profileDataWithFidoData = {
                            ...action.data,
                            firstName: fidoUserData.givenName,
                            lastName: fidoUserData.surName,
                            fullName:
                                fidoUserData.givenName +
                                " " +
                                fidoUserData.surName,
                            eAddress: fidoUserData.email,
                        };
                    }

                    this.localStorage.set(
                        "userProfile",
                        profileDataWithFidoData
                    );
                    this.auth.redirectUser(action.data);
                    this.snackBarService.success("User Profile Updated!");
                    return loginFormActions.userProfileRefresh();
                })
            )
    );
}
